/* eslint-disable react/no-multi-comp */
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Spin, Tabs, Table, Button, Row, Col, message, Modal } from 'antd';
import {
	ArrowLeftOutlined,
	DownloadOutlined,
	EyeOutlined,
	LeftOutlined,
	RightOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';
import ProgressBar from 'components/ProgressBar';
import {
	getClientInvoiceDetailV2,
	markClientInvoicePaymentV2,
	searchClientInvoiceTripsV2,
} from 'store/clientInvoiceSliceV2';
import { RCTable } from 'components';
import { getSignedUrl } from 'store/commonSlice';
import MarkPaymentModal from './components/MarkPaymentModal';
import EditablePayableChargesV2 from './components/EditablePayableChargesV2';
import InvoiceEditHistoryV2 from './components/InvoiceEditHistoryV2';

const CLIENT_INVOICE_ENTITY = 'client_invoice';
const TDS_AMOUNT_LABEL = 'TDS Amount';
const { TabPane } = Tabs;
const fileNameFromKey = (key = '') => {
	const match = String(key).match(
		/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}-/i
	);
	return match ? key.slice(match[0].length) : key;
};

const PageWrapper = styled(Box)`
	background: #f5f6f8;
	min-height: calc(100vh - 64px);
	padding-bottom: 40px;
`;

const HeaderBar = styled(Flex)`
	align-items: center;
	justify-content: space-between;
	background: #fff;
	padding: 16px 24px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
	gap: 16px;
`;

const BackArrow = styled(ArrowLeftOutlined)`
	font-size: 18px;
	cursor: pointer;
	color: #1f2937;
	flex-shrink: 0;
`;

const PageTitle = styled(Text)`
	font-size: 20px;
	font-weight: 700;
	color: #1f2937;
	display: block;
`;

const PageSubtitle = styled.div`
	font-size: 13px;
	color: #64748b;
	margin-top: 2px;
`;

const MarkPaymentButton = styled(Button)`
	background: #143e60;
	color: #ffffff;
	border: none;
	&:hover,
	&:focus {
		background: #1a4d7a !important;
		color: #ffffff !important;
		border: none;
	}
`;

const SummaryCard = styled(Box)`
	background: #fff;
	border-radius: 8px;
	border-top: 2px solid #2f8ecb;
	padding: 24px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
`;

const SummaryTitle = styled.div`
	font-size: 18px;
	font-weight: 700;
	text-transform: uppercase;
	color: #333;
	letter-spacing: 0.5px;
	margin-bottom: 20px;
`;

const FieldLabel = styled.div`
	font-size: 12px;
	font-weight: 600;
	color: #666;
	text-transform: uppercase;
	letter-spacing: 0.3px;
	margin-bottom: 6px;
`;

const FieldValue = styled.div`
	font-size: 15px;
	font-weight: 500;
	color: #333;
`;

const PaymentStatusContainer = styled.div`
	margin-top: 20px;
	padding-top: 20px;
	border-top: 1px solid #e8e8e8;
`;

const StatusHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
	flex-wrap: wrap;
	gap: 12px;
`;

const StatusLabel = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #333;
	text-transform: uppercase;
	letter-spacing: 0.3px;
`;

const Breakdown = styled.div`
	display: flex;
	gap: 24px;
	align-items: center;
`;

const Amount = styled.span`
	font-size: 15px;
	font-weight: 700;
	color: ${(props) => props.color || '#333'};
`;

const Pill = styled.span`
	display: inline-block;
	padding: 4px 12px;
	border-radius: 4px;
	font-size: 11px;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	background: ${(props) => props.bg || '#f1f5f9'};
	color: ${(props) => props.color || '#475569'};
`;

const TripItem = styled.div`
	min-width: 80px;
`;

const Card = styled(Box)`
	background: #fff;
	border-radius: 8px;
	padding: 20px 24px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const CardTitle = styled(Text)`
	font-size: 16px;
	font-weight: 600;
	color: #1e293b;
	display: block;
	margin-bottom: 16px;
`;
const TripTableWrap = styled.div`
	.ant-pagination {
		position: static;
		float: none;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		margin: 12px 0 0;
	}
`;

const RemainingBalanceBox = styled.div`
	margin-top: 24px;
	padding-top: 16px;
	border-top: 1px solid #e8e8e8;
`;

const RemainingBalanceLabel = styled.div`
	font-size: 16px;
	font-weight: 500;
	color: #333;
`;

const RemainingBalanceValue = styled.div`
	font-size: 24px;
	font-weight: 700;
	color: #ff9800;
	margin-top: 4px;
`;

const formatCurrency = (value) => {
	if (value == null || value === '') return '₹ 0.00';
	const num = Number(value);
	return Number.isNaN(num)
		? value
		: `₹ ${num.toLocaleString('en-IN', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
		  })}`;
};

const normalise = (v) => (v || '').toString().trim().toLowerCase().replace(/\s+/g, '_');

// No payment logged yet: show "Pending (100%)" instead of "Unpaid (0%)".
const derivePaymentDisplay = (paymentStatus, paid, paidPct) => {
	const noPaymentYet = paid <= 0 && normalise(paymentStatus) === 'unpaid';
	return {
		label: noPaymentYet ? 'Pending' : paymentStatus || 'Unpaid',
		pct: noPaymentYet ? 100 : paidPct,
	};
};
const canMarkPaymentFor = (invoice, pending) => {
	if (invoice.invoice_status !== 'Active') return false;
	const fullyPaid = normalise(invoice.payment_status) === 'paid' || Number(pending) <= 0;
	return !fullyPaid;
};

const PAYMENT_PILL = {
	paid: { bg: '#e6ffed', color: '#4caf50' },
	partially_paid: { bg: '#fff9e6', color: '#ff9800' },
	unpaid: { bg: '#fdecea', color: '#e5484d' },
	pending: { bg: '#fdecea', color: '#e5484d' },
};
const INVOICE_PILL = {
	active: { bg: '#e6ffed', color: '#16a34a' },
	cancellation_pending: { bg: '#fff9e6', color: '#ff9800' },
	cancelled: { bg: '#fdecea', color: '#e5484d' },
};

const pick = (record, keys) => {
	for (let i = 0; i < keys.length; i += 1) {
		const v = record?.[keys[i]];
		if (v !== undefined && v !== null && v !== '') return v;
	}
	return null;
};
const renderText = (keys) => (_, record) => pick(record, keys) ?? '-';
const renderMoney = (keys) => (_, record) => formatCurrency(pick(record, keys));
const renderMoneyOrDash = (keys) => (_, record) => {
	const v = pick(record, keys);
	return v == null ? '-' : formatCurrency(v);
};
const dateOnly = (v) => {
	if (!v) return '-';
	const [datePart] = String(v).split('T');
	const p = datePart.split(' ')[0].split('-');
	return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : datePart;
};
const renderDate = (keys) => (_, record) => dateOnly(pick(record, keys));

// Regular trips — mirrors the vendor invoice columns.
// Per-trip Trip Details table (RCTable + server pagination), mirrors the vendor bill trips tab.
const CLIENT_INVOICE_TRIP_COLUMNS = [
	{ title: 'Trip ID', dataIndex: 'trip_id', key: 'trip_id', width: 120 },
	{ title: 'Trip Date', dataIndex: 'reporting_date', key: 'reporting_date', width: 150 },
	{ title: 'Trip Type', dataIndex: 'trip_type', key: 'trip_type', width: 110 },
	{
		title: 'Project Name',
		dataIndex: 'project',
		key: 'project',
		width: 160,
		render: (t) => t || '-',
	},
	{
		title: 'Vehicle Type (Vehicle No)',
		key: 'vehicle_info',
		width: 200,
		render: (_, r) =>
			`${r.vehicle_type || '-'}${r.vehicle_number ? ` - ${r.vehicle_number}` : ''}`,
	},
	{
		title: 'Client Rate',
		dataIndex: 'client_rate',
		key: 'client_rate',
		width: 120,
		render: (t) => t ?? '-',
	},
	{
		title: 'Payable Amount',
		dataIndex: 'client_trip_cost',
		key: 'client_trip_cost',
		width: 140,
		render: (t) => t ?? '-',
	},
	{
		title: 'Actions',
		key: 'actions',
		width: 80,
		fixed: 'right',
		render: (_, r) => {
			const tripId = r.trip_id || r.id;
			if (!tripId) return '-';
			return (
				<Link
					to={`/trips/${tripId}/overview`}
					style={{ color: '#1890ff', fontSize: 16 }}
					title="View Trip Details"
				>
					<EyeOutlined />
				</Link>
			);
		},
	},
];

const isPdfName = (name = '') => name.split('.').pop().toLowerCase() === 'pdf';

function DocsCell({ record }) {
	const dispatch = useDispatch();
	const [viewer, setViewer] = useState({ open: false, index: 0, loading: false, urls: [] });
	const imageKeys = Array.isArray(record?.image_keys) ? record.image_keys : [];
	const keys = record?.customer_debit_proof_key
		? [...imageKeys, record.customer_debit_proof_key]
		: imageKeys;
	if (!keys.length) return <Text>-</Text>;

	const signedUrls = async (urlType) => {
		const res = await dispatch(
			getSignedUrl({ url_type: urlType, data: { entity: CLIENT_INVOICE_ENTITY, keys } })
		);
		return (res?.data || []).map((d) => d?.url || null);
	};

	const openViewer = async () => {
		setViewer({ open: true, index: 0, loading: true, urls: [] });
		const urls = await signedUrls('view');
		if (!urls.some(Boolean)) {
			setViewer({ open: false, index: 0, loading: false, urls: [] });
			message.error('Could not open the files.');
			return;
		}
		setViewer({ open: true, index: 0, loading: false, urls });
	};
	const closeViewer = () => setViewer({ open: false, index: 0, loading: false, urls: [] });
	const go = (delta) =>
		setViewer((v) => ({
			...v,
			index: Math.min(Math.max(v.index + delta, 0), keys.length - 1),
		}));

	const downloadAll = async () => {
		const urls = await signedUrls('download');
		if (!urls.some(Boolean)) {
			message.error('Could not download the files.');
			return;
		}
		urls.forEach((url, i) => {
			if (!url) return;
			const link = document.createElement('a');
			link.href = url;
			link.download = fileNameFromKey(keys[i]) || 'document';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		});
	};

	const currentKey = keys[viewer.index];
	const currentUrl = viewer.urls[viewer.index];
	const multiple = keys.length > 1;
	const arrowStyle = (side) => ({
		position: 'absolute',
		top: '50%',
		[side]: 8,
		transform: 'translateY(-50%)',
		zIndex: 2,
	});

	return (
		<Flex alignItems="center" style={{ gap: 2 }}>
			<Button
				type="link"
				size="small"
				icon={<EyeOutlined />}
				title="View proof(s)"
				onClick={openViewer}
			/>
			<Button
				type="link"
				size="small"
				icon={<DownloadOutlined />}
				title="Download proof(s)"
				onClick={downloadAll}
			/>
			<Modal
				open={viewer.open}
				title={`${fileNameFromKey(currentKey || '')}${
					multiple ? `  (${viewer.index + 1}/${keys.length})` : ''
				}`}
				footer={null}
				width={900}
				onCancel={closeViewer}
				destroyOnClose
			>
				{viewer.loading && (
					<div style={{ textAlign: 'center', padding: 40 }}>
						<Spin size="large" tip="Loading document..." />
					</div>
				)}
				{!viewer.loading && currentUrl && (
					<div style={{ position: 'relative', textAlign: 'center' }}>
						{multiple && (
							<Button
								shape="circle"
								icon={<LeftOutlined />}
								disabled={viewer.index === 0}
								onClick={() => go(-1)}
								style={arrowStyle('left')}
							/>
						)}
						{isPdfName(currentKey) ? (
							<iframe
								title={currentKey}
								src={`${currentUrl}#toolbar=0&navpanes=0&scrollbar=0`}
								style={{ width: '100%', height: '70vh', border: 'none' }}
							/>
						) : (
							<img
								src={currentUrl}
								alt={currentKey}
								style={{
									maxWidth: '100%',
									maxHeight: '70vh',
									objectFit: 'contain',
								}}
							/>
						)}
						{multiple && (
							<Button
								shape="circle"
								icon={<RightOutlined />}
								disabled={viewer.index === keys.length - 1}
								onClick={() => go(1)}
								style={arrowStyle('right')}
							/>
						)}
					</div>
				)}
			</Modal>
		</Flex>
	);
}

DocsCell.propTypes = {
	record: PropTypes.object,
};

const renderDocs = (_, record) => <DocsCell record={record} />;

// Mirrors the vendor invoice Payment History columns.
const PAYMENT_COLUMNS = [
	{ title: 'Amount', key: 'amount', render: renderMoney(['amount', 'paid_amount']) },
	{ title: 'UTR No', key: 'utr_number', render: renderText(['utr_number']) },
	{
		title: 'Customer Debit',
		key: 'customer_debit',
		render: renderMoneyOrDash(['customer_debit']),
	},
	{ title: TDS_AMOUNT_LABEL, key: 'tds_amount', render: renderMoneyOrDash(['tds_amount']) },
	{ title: 'Paid At', key: 'paid_at', render: renderDate(['paid_date', 'created_at']) },
	{ title: 'Paid By', key: 'paid_by', render: renderText(['paid_by', 'created_by_name']) },
	{ title: 'Docs', key: 'docs', render: renderDocs },
];

const PREVIOUS_PAYMENT_COLUMNS = [
	{ title: 'Amount', key: 'amount', render: renderMoney(['amount', 'paid_amount']) },
	{ title: 'UTR No', key: 'utr_number', render: renderText(['utr_number']) },
	{ title: 'Paid At', key: 'paid_at', render: renderDate(['paid_date', 'created_at']) },
	{ title: 'Version', key: 'version', render: renderText(['version']) },
	{ title: 'Docs', key: 'docs', render: renderDocs },
];
function PreviousPaymentsCard({ rows }) {
	if (!rows.length) return null;
	return (
		<Card mt="20px">
			<CardTitle>Previous Bill Payments</CardTitle>
			<Table
				rowKey={(r, i) => `prev-pay-${i}`}
				columns={PREVIOUS_PAYMENT_COLUMNS}
				dataSource={rows}
				pagination={false}
				size="small"
				locale={{ emptyText: 'No previous payments' }}
			/>
		</Card>
	);
}

PreviousPaymentsCard.propTypes = {
	rows: PropTypes.arrayOf(PropTypes.object),
};

PreviousPaymentsCard.defaultProps = {
	rows: [],
};

function ClientInvoiceDetailPageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();

	const [loading, setLoading] = useState(false);
	const [detail, setDetail] = useState(null);
	const [paymentOpen, setPaymentOpen] = useState(false);
	const [saving, setSaving] = useState(false);

	const fetchDetail = useCallback(() => {
		setLoading(true);
		dispatch(getClientInvoiceDetailV2({ id }))
			.then((res) => setDetail(res))
			.finally(() => setLoading(false));
	}, [dispatch, id]);

	useEffect(() => {
		fetchDetail();
	}, [fetchDetail]);

	const invoice = detail?.invoice || {};
	const trips = detail?.trips || { regular: [], adhoc: [] };
	const payments = detail?.payments || [];
	const previousPayments = detail?.previous_payments;

	const grandTotal = Number(invoice.grand_total) || 0;
	const paid = Number(invoice.paid) || 0;
	const pending = invoice.pending != null ? Number(invoice.pending) : grandTotal - paid;
	const paidPct = grandTotal > 0 ? Math.round((paid / grandTotal) * 100) : 0;

	const regularCount = trips.regular?.length || 0;
	const adhocCount = trips.adhoc?.length || 0;
	const totalTrips = regularCount + adhocCount;

	const [regPaginate, setRegPaginate] = useState({ limit: 10, offset: 0, currentPage: 1 });
	const [regTripData, setRegTripData] = useState({ rows: [], count: 0 });
	const [adhocPaginate, setAdhocPaginate] = useState({ limit: 10, offset: 0, currentPage: 1 });
	const [adhocTripData, setAdhocTripData] = useState({ rows: [], count: 0 });
	const regTripsDispatch = useCallback(
		(params) => searchClientInvoiceTripsV2({ ...params, id, trip_type: 'regular' }),
		[id]
	);
	const adhocTripsDispatch = useCallback(
		(params) => searchClientInvoiceTripsV2({ ...params, id, trip_type: 'adhoc' }),
		[id]
	);

	const { label: paymentStatusLabel, pct: paymentPctDisplay } = derivePaymentDisplay(
		invoice.payment_status,
		paid,
		paidPct
	);
	const paymentPill = PAYMENT_PILL[normalise(paymentStatusLabel)] || PAYMENT_PILL.unpaid;
	const invoicePill = INVOICE_PILL[normalise(invoice.invoice_status)];
	const canMarkPayment = canMarkPaymentFor(invoice, pending);

	const handleMarkPayment = (req_data) => {
		setSaving(true);
		dispatch(markClientInvoicePaymentV2({ req_data, setErrors: () => {} }))
			.then((res) => {
				if (res?.status === 'success') {
					setPaymentOpen(false);
					fetchDetail();
				}
			})
			.catch(() => {})
			.finally(() => setSaving(false));
	};

	const downloadPdf = () => {
		if (invoice.pdf_url) window.open(invoice.pdf_url);
		else message.info('PDF is not available yet.');
	};

	const goRegenerate = () => navigate(`/client-invoices/${invoice.id || id}/regenerate`);

	return (
		<PageWrapper>
			<HeaderBar>
				<Flex alignItems="center" style={{ gap: 16 }}>
					<BackArrow onClick={() => navigate('/client-invoices')} />
					<Box>
						<PageTitle>Client Invoice: {invoice.invoice_no || id || ''}</PageTitle>
						{invoice.client ? <PageSubtitle>{invoice.client}</PageSubtitle> : null}
					</Box>
				</Flex>
				<Flex style={{ gap: 10 }}>
					<Button icon={<DownloadOutlined />} onClick={downloadPdf}>
						Download PDF
					</Button>
					{invoice.invoice_status === 'Active' && (
						<Button onClick={goRegenerate}>Regenerate</Button>
					)}
					{canMarkPayment && (
						<MarkPaymentButton onClick={() => setPaymentOpen(true)}>
							Mark Payment
						</MarkPaymentButton>
					)}
				</Flex>
			</HeaderBar>

			<Spin spinning={loading}>
				<Box p="24px">
					<SummaryCard>
						<SummaryTitle>Invoice Summary</SummaryTitle>
						<Row gutter={[24, 12]}>
							<Col xs={24} sm={12} md={6}>
								<FieldLabel>Client</FieldLabel>
								<FieldValue>{invoice.client || '-'}</FieldValue>
							</Col>
							<Col xs={24} sm={12} md={6}>
								<FieldLabel>Project</FieldLabel>
								<FieldValue>{invoice.project || '-'}</FieldValue>
							</Col>
							<Col xs={24} sm={12} md={6}>
								<FieldLabel>Vehicle Type</FieldLabel>
								<FieldValue>
									{(invoice.vehicle_types || []).join(', ') || '-'}
								</FieldValue>
							</Col>
							<Col xs={24} sm={12} md={6}>
								<FieldLabel>Billing Period</FieldLabel>
								<FieldValue>{invoice.billing_period || '-'}</FieldValue>
							</Col>
						</Row>

						<PaymentStatusContainer>
							<StatusHeader>
								<StatusLabel>Payment Status:</StatusLabel>
								<Breakdown>
									<span>
										<FieldLabel as="span" style={{ marginRight: 4 }}>
											Total Amount:
										</FieldLabel>
										<Amount>{formatCurrency(grandTotal)}</Amount>
									</span>
									<span>
										<FieldLabel as="span" style={{ marginRight: 4 }}>
											Pending Amount:
										</FieldLabel>
										<Amount color="#ff9800">{formatCurrency(pending)}</Amount>
									</span>
									<span>
										<FieldLabel as="span" style={{ marginRight: 4 }}>
											Paid:
										</FieldLabel>
										<Amount color="#4caf50">{formatCurrency(paid)}</Amount>
									</span>
								</Breakdown>
							</StatusHeader>

							<ProgressBar percentage={paidPct} height={8} spliting={4} />

							<Flex
								alignItems="center"
								justifyContent="space-between"
								mt={3}
								flexWrap="wrap"
								style={{ gap: 16 }}
							>
								<Flex alignItems="center" flexWrap="wrap" style={{ gap: 32 }}>
									<Flex alignItems="center" style={{ gap: 8 }}>
										<Pill bg={paymentPill.bg} color={paymentPill.color}>
											{paymentStatusLabel}
										</Pill>
										<Text style={{ color: paymentPill.color, fontWeight: 600 }}>
											({paymentPctDisplay} %)
										</Text>
									</Flex>
									<TripItem>
										<FieldLabel>Total Trips</FieldLabel>
										<FieldValue>{totalTrips}</FieldValue>
									</TripItem>
									<TripItem>
										<FieldLabel>Regular</FieldLabel>
										<FieldValue>{regularCount}</FieldValue>
									</TripItem>
									<TripItem>
										<FieldLabel>Ad-hoc</FieldLabel>
										<FieldValue>{adhocCount}</FieldValue>
									</TripItem>
								</Flex>
								{invoice.invoice_status &&
								invoice.invoice_status !== 'Regeneration Pending' ? (
									<Pill bg={invoicePill?.bg} color={invoicePill?.color}>
										{invoice.invoice_status}
									</Pill>
								) : null}
							</Flex>
						</PaymentStatusContainer>
					</SummaryCard>

					<Card mt="20px">
						<CardTitle>Trip Details</CardTitle>
						<Tabs defaultActiveKey="regular">
							<TabPane tab={`Regular (${regularCount})`} key="regular">
								<TripTableWrap>
									<RCTable
										columns={CLIENT_INVOICE_TRIP_COLUMNS}
										dispatchAction={regTripsDispatch}
										togglePaginate={[regPaginate, setRegPaginate]}
										tableDatas={[regTripData, setRegTripData]}
										configs={{ staticPayload: { id, trip_type: 'regular' } }}
										rowKey={(r) => r.id || r.trip_id}
										scroll={{ x: true }}
										size="small"
										locale={{ emptyText: 'No regular trips found' }}
									/>
								</TripTableWrap>
							</TabPane>
							<TabPane tab={`Ad-hoc (${adhocCount})`} key="adhoc">
								<TripTableWrap>
									<RCTable
										columns={CLIENT_INVOICE_TRIP_COLUMNS}
										dispatchAction={adhocTripsDispatch}
										togglePaginate={[adhocPaginate, setAdhocPaginate]}
										tableDatas={[adhocTripData, setAdhocTripData]}
										configs={{ staticPayload: { id, trip_type: 'adhoc' } }}
										rowKey={(r) => r.id || r.trip_id}
										scroll={{ x: true }}
										size="small"
										locale={{ emptyText: 'No ad-hoc trips found' }}
									/>
								</TripTableWrap>
							</TabPane>
						</Tabs>
					</Card>

					<Card mt="20px">
						<CardTitle>Tax Invoice</CardTitle>
						{invoice.pdf_url ? (
							<div
								style={{
									border: '1px solid #e5e7eb',
									borderRadius: 8,
									overflow: 'hidden',
									maxWidth: 720,
									margin: '0 auto',
								}}
							>
								<iframe
									src={`${invoice.pdf_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
									title="Tax Invoice"
									style={{ width: '100%', height: 760, border: 'none' }}
								/>
							</div>
						) : (
							<div
								style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0' }}
							>
								Tax invoice PDF is not available yet.
							</div>
						)}
					</Card>

					<EditablePayableChargesV2 invoice={invoice} />

					<InvoiceEditHistoryV2 invoiceId={invoice.id || id} />

					<Card mt="20px">
						<CardTitle>Payment History</CardTitle>
						<Table
							rowKey={(r, i) => `pay-${i}`}
							columns={PAYMENT_COLUMNS}
							dataSource={payments}
							pagination={false}
							size="small"
							locale={{ emptyText: 'No payment history found' }}
						/>
						<RemainingBalanceBox>
							<RemainingBalanceLabel>Remaining Balance:</RemainingBalanceLabel>
							<RemainingBalanceValue>{formatCurrency(pending)}</RemainingBalanceValue>
						</RemainingBalanceBox>
					</Card>

					<PreviousPaymentsCard rows={previousPayments} />
				</Box>
			</Spin>

			<MarkPaymentModal
				visible={paymentOpen}
				invoice={invoice}
				payments={payments}
				saving={saving}
				onSave={handleMarkPayment}
				onClose={() => setPaymentOpen(false)}
			/>
		</PageWrapper>
	);
}

export default ClientInvoiceDetailPageV2;
